/**
 * Crear una cuenta — `URS §7`, `OD-0027 D-01..D-04`.
 *
 * La API responde `202` IDÉNTICO exista o no la cuenta, y esta pantalla dice
 * lo mismo en los dos casos. Lo único que puede rechazar es lo ESCRITO
 * (contraseña fuera de la política, límite de intentos), nunca la existencia
 * de la cuenta.
 */
import { useState } from "react";
import { api, ESTADO_VALIDACION, type PlanDePago } from "../api/cliente";
import { t } from "../i18n/es";
import { contrasenaCorta, MINIMO_CONTRASENA } from "../ui/contrasena";
import { Aviso, Boton, Campo, Tarjeta } from "../ui/primitivas";

export function PantallaRegistro({
  hayConexion,
  planElegido = null,
  origen = null,
  deAsesoria = false,
  emailInicial = null,
  onVolver,
}: {
  hayConexion: boolean;
  /** El plan de pago elegido en faktelio.com. Solo cambia lo que se cuenta. */
  planElegido?: PlanDePago | null;
  /** La pagina y el boton de faktelio.com de los que viene (`enlaces/origen-de-alta.ts`). */
  origen?: string | null;
  /** Llega por el enlace de una gestoría (`enlaces/asesoria-del-enlace.ts`). Solo cambia lo que se cuenta. */
  deAsesoria?: boolean;
  /** Llega desde «Reenviar enlace» del login, con el email que escribió allí. */
  emailInicial?: string | null;
  onVolver: () => void;
}) {
  const avisoDelPlan =
    planElegido === null ? null : (
      <Aviso tono="info" titulo={t.plan.elegido[planElegido]}>
        {t.registro.planElegidoDetalle}
      </Aviso>
    );

  const [email, setEmail] = useState(emailInicial ?? "");
  const [password, setPassword] = useState("");
  const [errorEmail, setErrorEmail] = useState<string | null>(null);
  const [errorPassword, setErrorPassword] = useState<string | null>(null);
  const [errorGeneral, setErrorGeneral] = useState<string | null>(null);
  const [enviando, setEnviando] = useState(false);
  const [enviado, setEnviado] = useState(false);

  async function enviar(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    setErrorGeneral(null);
    setErrorEmail(email.trim() === "" ? t.login.faltaEmail : null);
    const errorDeLaContrasena =
      password === "" ? t.contrasenaNueva.faltaPassword : contrasenaCorta(password) ? t.contrasenaNueva.corta(MINIMO_CONTRASENA) : null;
    setErrorPassword(errorDeLaContrasena);
    if (email.trim() === "" || errorDeLaContrasena !== null) return;

    setEnviando(true);
    const r = await api.registro(email.trim(), password, origen);
    setEnviando(false);

    if (r.ok) {
      setPassword("");
      setEnviado(true);
      return;
    }
    if (r.estado === 0) setErrorGeneral(t.comun.sinRed);
    else if (r.estado === 429) setErrorGeneral(t.comun.demasiadosIntentos);
    // 422 con mensaje: contraseña fuera de la política. La API sabe el mínimo
    // vigente (OD-0027 D-01); sin mensaje, es el email el que no tiene forma.
    else if (r.estado === ESTADO_VALIDACION && r.error !== null && r.error.message.includes("contraseña")) {
      setErrorPassword(r.error.message);
    } else if (r.estado === ESTADO_VALIDACION) setErrorEmail(t.registro.emailNoValido);
    else setErrorGeneral(t.comun.errorGenerico);
  }

  if (enviado) {
    /*
      EL SALTO AL BUZON (auditoria de conversion, 2026-10-27): un enlace a los
      dos buzones mas usados y la forma de pedir otro enlace. Pedirlo es volver
      al formulario con el email ya escrito: un alta nueva sustituye a la
      pendiente (`RegistrationService.register`) y gasta el mismo limite de
      intentos, asi que no abre ninguna puerta que el formulario no abra ya.
    */
    return (
      <Tarjeta titulo={t.registro.enviadoTitulo}>
        <Aviso tono="exito">
          {t.registro.enviadoDetalle(email.trim())}
        </Aviso>
        <div className="fila-botones">
          <span>{t.registro.abrirBuzon}</span>
          {t.registro.buzones.map((b) => (
            <a key={b.nombre} className="boton boton-secundario enlace-boton" href={b.url} target="_blank" rel="noopener noreferrer">
              {b.nombre}
            </a>
          ))}
        </div>
        {planElegido === null ? null : <p className="ayuda-neutra">{t.registro.planElegidoDespues}</p>}
        <p className="ayuda-neutra">{t.registro.noLlega}</p>
        <Boton variante="secundario" onClick={() => setEnviado(false)}>
          {t.registro.volverAPedir}
        </Boton>
        <Boton variante="terciario" onClick={onVolver}>
          {t.recuperar.volver}
        </Boton>
      </Tarjeta>
    );
  }

  return (
    <Tarjeta titulo={emailInicial !== null ? t.registro.reenvioTitulo : t.registro.titulo}>
      {avisoDelPlan}
      {emailInicial !== null ? <Aviso tono="info">{t.registro.reenvioDetalle}</Aviso> : null}
      {planElegido === null && emailInicial === null ? (
        <ul className="registro-ventajas">
          {t.registro.ventajas.map((v) => <li key={v}>{v}</li>)}
        </ul>
      ) : null}
      {deAsesoria ? (
        <Aviso tono="info" titulo={t.registro.deAsesoriaTitulo}>
          {t.registro.deAsesoriaDetalle}
        </Aviso>
      ) : null}
      <form onSubmit={enviar} noValidate>
        <Campo
          id="registro-email"
          etiqueta={t.login.email}
          valor={email}
          onChange={(v) => {
            setEmail(v);
            setErrorEmail(null);
          }}
          tipo="email"
          inputMode="email"
          autoComplete="email"
          error={errorEmail}
          autoFocus={emailInicial === null}
        />
        <Campo
          id="registro-password"
          etiqueta={t.login.password}
          valor={password}
          onChange={(v) => {
            setPassword(v);
            setErrorPassword(null);
          }}
          tipo="password"
          mostrar={t.verContrasena}
          autoComplete="new-password"
          ayuda={t.contrasenaNueva.ayuda}
          autoFocus={emailInicial !== null}
          error={errorPassword}
        />
        <p className="ayuda-neutra">
          {t.registro.legalAntes}{" "}
          <a href={t.registro.legalCondicionesUrl} target="_blank" rel="noreferrer">
            {t.registro.legalCondiciones}
          </a>{" "}
          {t.registro.legalEntre}{" "}
          <a href={t.registro.legalPrivacidadUrl} target="_blank" rel="noreferrer">
            {t.registro.legalPrivacidad}
          </a>{" "}
          {t.registro.legalDespues}
        </p>
        {errorGeneral !== null ? <Aviso tono="error">{errorGeneral}</Aviso> : null}
        {!hayConexion ? <Aviso tono="aviso">{t.comun.sinRed}</Aviso> : null}
        <Boton tipo="submit" deshabilitado={enviando || !hayConexion}>
          {enviando ? t.registro.creando : t.registro.crear}
        </Boton>
      </form>
      <Boton variante="secundario" onClick={onVolver}>
        {t.registro.yaTengoCuenta}
      </Boton>
    </Tarjeta>
  );
}
